import { createSelector } from 'reselect';

import { selectGetConfigValue } from '@pressreader/config';
import { DeviceType, ISwiperConfig, ScreenSizeType } from '@pressreader/ui-types';

import { MODULE_NAME } from '../constants';
import { getIsCompactScreen } from '../utils';

import { IState } from './reducer';

const getState = (state: { [MODULE_NAME]: IState }) => state[MODULE_NAME];

export const selectDeviceInfo = createSelector(getState, state => state.device);
export const selectScreenInfo = createSelector(getState, state => state.screen);

// device.mobile sourced from the Redux store (not the static `device` object) so config can
// override it — overriding to false forces 'desktop' everywhere downstream (the AB#173904 goal).
export const selectIsMobileDevice = createSelector(selectDeviceInfo, deviceInfo => deviceInfo.mobile);

/**
 * Apple touch device (iPhone / iPad, including iPadOS-13+ masquerading as Mac) per the
 * store's device info. Store-sourced for the same reason as `selectIsMobileDevice`: a
 * `deviceInfoLoaded` override stays authoritative for every downstream gate, including
 * iOS-specific DOM interop such as the body-scroll-lock library wrappers.
 */
export const selectIsAppleTouchDevice = createSelector(selectDeviceInfo, deviceInfo => deviceInfo.iPad || deviceInfo.iPhone);

/**
 * Touch reading device — a phone or a tablet — per the store's device info: the phone-oriented UA
 * regex behind `deviceInfo.mobile`, plus the two vendor flags it misses. It has no `ipad` token and
 * iPadOS 13+ sends a Mac UA, so iPads only surface via `iPad`/`iPhone`; and its Android branch is
 * `(android).+mobile`, so Android *tablets*, whose UA drops the `Mobile` token, only surface via
 * `android`. Phones already match `mobile`, so `android` adds tablets without needing to tell the
 * two apart. Deliberately UA-based rather than capability-based: a touchscreen Windows laptop sets
 * none of these flags and stays a desktop, which is what the pointer-oriented layouts expect.
 * Store-sourced so a `deviceInfoLoaded` override stays authoritative, as with the selectors above.
 */
export const selectIsTouchDevice = createSelector(
    selectDeviceInfo,
    deviceInfo => deviceInfo.mobile || deviceInfo.iPad || deviceInfo.iPhone || deviceInfo.android,
);

export const selectScreenType = createSelector(selectScreenInfo, screenInfo => screenInfo.screenType);

/**
 * Mobile-screen contract: viewport classified as Small or smaller.
 */
export const selectIsMobileScreen = createSelector(selectScreenType, screenType => screenType <= ScreenSizeType.Small);

export const selectDeviceType = createSelector(selectIsMobileDevice, selectIsMobileScreen, (isMobileDevice, isMobileScreen): DeviceType => {
    if (!isMobileDevice) {
        return 'desktop';
    }

    if (isMobileScreen) {
        return 'phone';
    }

    return 'tablet';
});

/**
 * Phone form-factor contract: mobile device with a mobile-sized screen.
 */
export const selectIsPhone = createSelector(selectDeviceType, deviceType => deviceType === 'phone');

/**
 * Desktop form-factor contract: non-mobile device.
 */
export const selectIsDesktop = createSelector(selectDeviceType, deviceType => deviceType === 'desktop');

/**
 * Compact screen contract used by screen-width-only layout decisions.
 */
export const selectIsCompactScreen = createSelector(selectScreenType, (screenType): boolean => getIsCompactScreen(screenType));

/**
 * Compact view contract used by collapsible sidebars and other mobile-style layouts.
 */
export const selectIsCompactView = createSelector(
    selectDeviceType,
    selectIsCompactScreen,
    (deviceType, isCompactScreen) => deviceType === 'phone' || isCompactScreen,
);

export const selectScreenOrientation = createSelector(selectScreenInfo, screenInfo => screenInfo.orientation);

export const selectNotification = createSelector(getState, state => state.notification);

export const selectCommonSwiperConfig = createSelector(
    selectGetConfigValue,
    (getConfigValue): ISwiperConfig => ({
        threshold: getConfigValue('app.common.swiper.threshold', 0),
    }),
);

export const selectSigninEnabled = createSelector(
    selectGetConfigValue,
    getConfigValue => getConfigValue<boolean>('ui.Sidebar.SignInEnabled', true) && getConfigValue<boolean>('ui.TopToolbar.SignInEnabled', true),
);
